/**
 * Typed API client foundation.
 *
 * Auth model (Batch 25a onward, post-25c sweep):
 *   - Optional `getToken` callback on each call, supplied by a
 *     Clerk-aware React hook via `useAuth().getToken`. If absent OR
 *     resolves to null, the request fires without an Authorization
 *     header — the server returns 401 for authed endpoints, which
 *     the caller handles.
 *   - Anonymous endpoints (e.g. /v1/users/invitation/redeem) call
 *     `apiFetch` without a `getToken`. No Authorization header is sent.
 *
 * Error parsing:
 *   - API returns `{ error: { code, message, details? } }` per
 *     `API_CONTRACT.md` §7. apiFetch unwraps this envelope.
 *   - If the envelope is missing (e.g. plain 500, network failure),
 *     falls back to mapping HTTP status to a standard code.
 */
import type { ZodType } from 'zod';

const DEFAULT_DEV_BASE_URL = 'http://localhost:8787';

export type ApiErrorCode =
  | 'network_error'
  | 'validation_error'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'server_error'
  | 'unknown_error';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: ApiErrorCode | string;
  public readonly details?: unknown;

  constructor(
    status: number,
    code: ApiErrorCode | string,
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (details !== undefined) {
      this.details = details;
    }
  }
}

export function getApiBaseUrl(): string {
  // Vite inlines import.meta.env.* at build time. Use || (not ??) so that
  // an empty-string value falls through to the dev default — which is what
  // we want if the var is set but blank in a Cloudflare Pages config.
  return import.meta.env.VITE_API_BASE_URL || DEFAULT_DEV_BASE_URL;
}

interface ApiFetchOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /**
   * Optional Zod schema. If supplied, the response is parsed against it
   * and validation failures throw an ApiError with code 'validation_error'.
   */
  schema?: ZodType<T>;
  signal?: AbortSignal;
  headers?: HeadersInit;
  /**
   * Optional auth-token getter. When provided, the resolved token (if
   * non-null) is sent as a Bearer token. When omitted or the resolved
   * value is null, no Authorization header is sent — and the server
   * returns 401 for authed endpoints.
   *
   * In runtime code this is supplied by Clerk-aware hooks via
   * `useAuth().getToken`. In integration tests, a null-returning getter
   * drives the 401 path; a real-Clerk-test-user getter drives success.
   */
  getToken?: () => Promise<string | null>;
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions<T> = {},
): Promise<T> {
  const { method = 'GET', body, schema, signal, headers, getToken } = options;

  const token = getToken ? await getToken() : null;

  const finalHeaders = new Headers(headers);
  if (body !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  if (token) {
    finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}${path}`, {
      method,
      headers: finalHeaders,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal !== undefined ? { signal } : {}),
    });
  } catch (err) {
    throw new ApiError(0, 'network_error', 'Network request failed', err);
  }

  let payload: unknown = null;
  const text = await response.text();
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    // API error envelope per API_CONTRACT §7: { error: { code, message, details? } }
    // Falls back to status-based code if the envelope is missing.
    const errorEnvelope =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as {
            error?: { code?: string; message?: string; details?: unknown };
          }).error
        : null;
    const code = errorEnvelope?.code ?? mapStatusToCode(response.status);
    const message =
      errorEnvelope?.message ?? response.statusText ?? 'Request failed';
    throw new ApiError(
      response.status,
      code,
      message,
      errorEnvelope?.details,
    );
  }

  if (schema) {
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError(
        0,
        'validation_error',
        'Response failed validation',
        parsed.error,
      );
    }
    return parsed.data;
  }

  return payload as T;
}

function mapStatusToCode(status: number): ApiErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'unknown_error';
}
